import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BaseModal from '../base/BaseModal';
import { updateProfileData } from '../../features/userProfile/userProfileThunks';
import { selectUpdating } from '../../features/userProfile/userProfileSelectors';
import { showToast } from '../../utils/toastNotification';

const UserProfileModal = ({ isOpen, onClose, profile }) => {
    const [formData, setFormData] = useState({
        hoVaTen: '',
        soDienThoai: '',
    });

    const dispatch = useDispatch();
    const updating = useSelector(selectUpdating);

    const [phoneError, setPhoneError] = useState('');
    const [nameError, setNameError] = useState('');

    useEffect(() => {
        if (isOpen) {
            if (profile) {
                setFormData({
                    hoVaTen: profile.hoVaTen || '',
                    soDienThoai: profile.soDienThoai || '',
                });
            } else {
                setFormData({ hoVaTen: '', soDienThoai: '' });
            }
            setPhoneError('');
            setNameError('');
        }
    }, [profile, isOpen]);

    const handleClose = () => {
        if (profile) {
            setFormData({
                hoVaTen: profile.hoVaTen || '',
                soDienThoai: profile.soDienThoai || '',
            });
        } else {
            setFormData({ hoVaTen: '', soDienThoai: '' });
        }
        setPhoneError('');
        setNameError('');
        onClose();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'soDienThoai') {
            if (value !== '' && !/^\d*$/.test(value)) return;
            if (value.length > 11) return;
            if (phoneError) setPhoneError('');
        }
        if (name === 'hoVaTen') {
            if (nameError) setNameError('');
        }
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleNameBlur = () => {
        const name = (formData.hoVaTen || '').trim();
        if (!name) {
            setNameError('Họ và tên không được để trống');
        } else if (name.length < 2) {
            setNameError('Họ và tên phải có ít nhất 2 ký tự');
        } else if (name.length > 50) {
            setNameError('Họ và tên không được vượt quá 50 ký tự');
        } else {
            setNameError('');
        }
    };

    const handlePhoneBlur = () => {
        const phone = (formData.soDienThoai || '').trim();
        if (phone && !/^(03|05|07|08|09)\d{8}$|^02\d{8,9}$/.test(phone)) {
            setPhoneError('Số điện thoại không hợp lệ (gồm 10 số di động hoặc 10-11 số máy bàn bắt đầu bằng 02)');
        } else {
            setPhoneError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const currentHoVaTen = (formData.hoVaTen || '').trim();
        const currentSoDienThoai = (formData.soDienThoai || '').trim();
        const origHoVaTen = (profile?.hoVaTen || '').trim();
        const origSoDienThoai = (profile?.soDienThoai || '').trim();

        if (currentHoVaTen === origHoVaTen && currentSoDienThoai === origSoDienThoai) {
            showToast.info('Thông tin không có thay đổi nào để cập nhật');
            return;
        }

        if (!currentHoVaTen) {
            setNameError('Họ và tên không được để trống');
            showToast.error('Họ và tên không được để trống');
            return;
        }

        if (currentHoVaTen.length < 2) {
            const errorMsg = 'Họ và tên phải có ít nhất 2 ký tự';
            setNameError(errorMsg);
            showToast.error(errorMsg);
            return;
        }

        if (currentHoVaTen.length > 50) {
            const errorMsg = 'Họ và tên không được vượt quá 50 ký tự';
            setNameError(errorMsg);
            showToast.error(errorMsg);
            return;
        }

        if (currentSoDienThoai && !/^(03|05|07|08|09)\d{8}$|^02\d{8,9}$/.test(currentSoDienThoai)) {
            const errorMsg = 'Số điện thoại không hợp lệ (gồm 10 số di động hoặc 10-11 số máy bàn bắt đầu bằng 02)';
            setPhoneError(errorMsg);
            showToast.error(errorMsg);
            return;
        }

        try {
            const result = await dispatch(updateProfileData({
                hoVaTen: currentHoVaTen,
                soDienThoai: currentSoDienThoai,
            })).unwrap();
            showToast.success('Cập nhật hồ sơ thành công');
            setFormData({
                hoVaTen: result.hoVaTen || '',
                soDienThoai: result.soDienThoai || '',
            });
            onClose();
        } catch (err) {
            showToast.error(err || 'Lỗi cập nhật hồ sơ');
        }
    };

    if (!profile) {
        return null;
    }
    const p = profile;

    return (
        <BaseModal isOpen={isOpen} onClose={handleClose} title="Hồ sơ">
            <form onSubmit={handleSubmit} autoComplete="off" className="space-y-4">
                <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                        Tên đăng nhập
                    </label>
                    <input
                        type="text"
                        value={p.tenDangNhap || ''}
                        disabled
                        className="w-full px-2.5 md:px-3 py-1.5 md:py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 text-xs md:text-sm cursor-not-allowed"
                    />
                </div>

                <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                        Email
                    </label>
                    <input
                        type="email"
                        value={p.email || ''}
                        disabled
                        className="w-full px-2.5 md:px-3 py-1.5 md:py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 text-xs md:text-sm cursor-not-allowed"
                    />
                </div>

                <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                        Vai trò
                    </label>
                    <input
                        type="text"
                        value={p.vaiTro || ''}
                        disabled
                        className="w-full px-2.5 md:px-3 py-1.5 md:py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 text-xs md:text-sm cursor-not-allowed"
                    />
                </div>

                <div className="border-t border-gray-200 my-4"></div>

                <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                        Họ và tên
                    </label>
                    <input
                        type="text"
                        name="hoVaTen"
                        autoComplete="off"
                        value={formData.hoVaTen}
                        onChange={handleChange}
                        onBlur={handleNameBlur}
                        placeholder="Nhập họ và tên"
                        maxLength={50}
                        className={`w-full px-2.5 md:px-3 py-1.5 md:py-2 border rounded-lg text-xs md:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            nameError ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'
                        }`}
                    />
                    {nameError && (
                        <p className="mt-1 text-xs text-red-600">{nameError}</p>
                    )}
                </div>

                <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                        Số điện thoại
                    </label>
                    <input
                        type="tel"
                        name="soDienThoai"
                        autoComplete="off"
                        value={formData.soDienThoai}
                        onChange={handleChange}
                        onBlur={handlePhoneBlur}
                        placeholder="Ví dụ: 0901234567"
                        maxLength={11}
                        className={`w-full px-2.5 md:px-3 py-1.5 md:py-2 border rounded-lg text-xs md:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            phoneError ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'
                        }`}
                    />
                    {phoneError && (
                        <p className="mt-1 text-xs text-red-600">{phoneError}</p>
                    )}
                </div>

                <div className="flex gap-3 justify-end pt-4">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-1.5 md:py-2 text-xs md:text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 cursor-pointer"
                        disabled={updating}
                    >
                        Hủy
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-1.5 md:py-2 text-xs md:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        disabled={updating}
                    >
                        {updating ? 'Đang cập nhật...' : 'Cập nhật'}
                    </button>
                </div>
            </form>
        </BaseModal>
    );
};

export default UserProfileModal;